import React, { useEffect, useState } from 'react';
import api from '../api';

const KURS_TYPEN = [
  { value: 'pirat',         label: '🏴‍☠️ Pirat' },
  { value: 'seepferdchen',  label: '🐴 Seepferdchen' },
  { value: 'trixi',         label: '🌟 Trixi' },
  { value: 'bronze',        label: '🥉 Bronze' },
  { value: 'silber',        label: '🥈 Silber' },
  { value: 'gold',          label: '🥇 Gold' },
  { value: 'hai',           label: '🦈 Hai (Leistung)' },
  { value: 'la_silber',     label: '🦈🥈 LA Silber' },
  { value: 'la_gold',       label: '🦈🥇 LA Gold' },
];

const INIT_FORM = { titel: '', beschreibung: '' };

export default function Uebungen() {
  const [aktiveTab, setAktiveTab] = useState('pirat');
  const [uebungen, setUebungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(INIT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = (kursTyp) => {
    setLoading(true);
    setError('');
    api.get(`/api/admin/uebungen?kurs_typ=${kursTyp}`)
      .then(r => setUebungen(r.data))
      .catch(() => setError('Übungen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(aktiveTab); }, [aktiveTab]);

  const handleTabChange = (tab) => {
    setAktiveTab(tab);
    setShowForm(false);
    setEditId(null);
    setForm(INIT_FORM);
    setFormError('');
  };

  const openNew = () => {
    setEditId(null);
    setForm(INIT_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ titel: u.titel, beschreibung: u.beschreibung || '' });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.titel.trim()) { setFormError('Titel ist Pflichtfeld'); return; }
    setSaving(true); setFormError('');
    try {
      if (editId) {
        await api.put(`/api/admin/uebungen/${editId}`, { ...form, aktiv: true });
      } else {
        await api.post('/api/admin/uebungen', { ...form, kurs_typ: aktiveTab });
      }
      setShowForm(false);
      setEditId(null);
      setForm(INIT_FORM);
      load(aktiveTab);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Übung "${u.titel}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/admin/uebungen/${u.id}`);
      load(aktiveTab);
    } catch (err) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const handleReorder = async (uebungId, richtung) => {
    const idx = uebungen.findIndex(u => u.id === uebungId);
    if (idx < 0) return;
    const zielIdx = richtung === 'hoch' ? idx - 1 : idx + 1;
    if (zielIdx < 0 || zielIdx >= uebungen.length) return;

    const neu = [...uebungen];
    [neu[idx], neu[zielIdx]] = [neu[zielIdx], neu[idx]];
    setUebungen(neu);

    try {
      await api.put('/api/admin/uebungen/reorder', { reihenfolge: neu.map((u, i) => ({ id: u.id, reihenfolge: i + 1 })) });
    } catch {
      load(aktiveTab);
    }
  };

  const handleToggleAktiv = async (u) => {
    try {
      await api.put(`/api/admin/uebungen/${u.id}`, { titel: u.titel, beschreibung: u.beschreibung, aktiv: !u.aktiv });
      load(aktiveTab);
    } catch {
      setError('Konnte nicht gespeichert werden');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Prüfungsanforderungen</h2>
      </div>

      {/* Kurs-Typ-Tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {KURS_TYPEN.map(k => (
          <button key={k.value} onClick={() => handleTabChange(k.value)}
            className={`btn btn-sm ${aktiveTab === k.value ? 'btn-primary' : 'btn-secondary'}`}>
            {k.label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div style={{ padding: '0.875rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontWeight: 700 }}>
            {KURS_TYPEN.find(k => k.value === aktiveTab)?.label} – Übungen
          </span>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Übung hinzufügen</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <>
            {uebungen.length === 0 && !showForm && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Noch keine Übungen definiert. Klick auf „+ Übung hinzufügen" um zu beginnen.
              </div>
            )}

            {uebungen.map((u, i) => (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                padding: '0.875rem 1rem',
                borderBottom: '1px solid var(--border)',
                opacity: u.aktiv ? 1 : 0.5,
              }}>
                {/* Reihenfolge-Steuerung */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                  <button disabled={i === 0} onClick={() => handleReorder(u.id, 'hoch')}
                    style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 4, width: 24, height: 22, cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, fontSize: '0.7rem' }}>▲</button>
                  <button disabled={i === uebungen.length - 1} onClick={() => handleReorder(u.id, 'runter')}
                    style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 4, width: 24, height: 22, cursor: i === uebungen.length - 1 ? 'default' : 'pointer', opacity: i === uebungen.length - 1 ? 0.3 : 1, fontSize: '0.7rem' }}>▼</button>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#999', minWidth: 18 }}>{i + 1}.</span>
                    {u.titel}
                    {!u.aktiv && <span style={{ fontSize: '0.72rem', background: '#f5f5f5', color: '#999', padding: '1px 6px', borderRadius: 10 }}>Inaktiv</span>}
                  </div>
                  {u.beschreibung && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{u.beschreibung}</div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)} style={{ fontSize: '0.75rem' }}>Bearbeiten</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleToggleAktiv(u)} style={{ fontSize: '0.75rem' }}>
                    {u.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u)} style={{ fontSize: '0.75rem' }}>✕</button>
                </div>
              </div>
            ))}

            {/* Inline-Formular */}
            {showForm && (
              <div style={{ padding: '1rem', background: '#f8f9fa', borderTop: '2px solid var(--primary)' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  {editId ? 'Übung bearbeiten' : 'Neue Übung'}
                </div>
                {formError && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{formError}</div>}
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label className="form-label required">Titel</label>
                    <input type="text" className="form-input" value={form.titel}
                      onChange={e => setForm(f => ({ ...f, titel: e.target.value }))}
                      placeholder="z. B. 25m Kraulschwimmen" autoFocus />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Beschreibung / Hinweis</label>
                    <input type="text" className="form-input" value={form.beschreibung}
                      onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
                      placeholder="z. B. ohne Unterbrechung in korrekter Technik" />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => { setShowForm(false); setEditId(null); }}>Abbrechen</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                      {saving ? 'Speichern…' : editId ? 'Speichern' : 'Hinzufügen'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
